/**
 * Spaltenfilter fuer Tabellen – eine Eingabezeile direkt unter den
 * Ueberschriften, wie man sie aus ACMP oder Excel kennt.
 *
 * Warum im Browser und nicht im Server: Die Filter sollen beim Tippen
 * reagieren. Eine Anfrage je Tastendruck waere langsamer und wuerde die
 * Auswertungen unnoetig belasten. Der Preis dafuer ist eine Bedingung, die
 * ernst genommen werden muss:
 *
 *   Ein Filter im Browser darf nur ueber Zeilen laufen, die AUCH VOLLSTAENDIG
 *   geladen sind.
 *
 * Sonst behauptet die Tabelle "kein Treffer", obwohl auf Seite 3 etwas steht.
 * Serverseitig paginierte Seiten muessen deshalb waehrend eines aktiven
 * Filters alle Zeilen holen; `truncated` macht sichtbar, wenn selbst das nicht
 * gereicht hat.
 *
 * Verglichen wird ohne Ruecksicht auf Gross-/Kleinschreibung und auf
 * diakritische Zeichen: Wer "muller" tippt, meint "Müller".
 */
import { Button, Input, TableHeaderCell, TableRow, makeStyles } from '@fluentui/react-components';
import { tokens } from '@fluentui/react-components';
import { DismissRegular, FilterRegular } from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';
import { filterRows, type FilterColumn } from '../lib/columnFilter.js';
import { useI18n } from '../i18n/index.js';

export type { FilterColumn } from '../lib/columnFilter.js';

export interface ColumnFilterState<T> {
  /** Aktuelle Eingaben je Spaltenschluessel. */
  values: Record<string, string>;
  setValue: (key: string, value: string) => void;
  /** true, sobald mindestens ein Feld gefuellt ist. */
  active: boolean;
  clear: () => void;
  /** Wendet alle gesetzten Filter an. Reihenfolge bleibt erhalten. */
  apply: (rows: readonly T[]) => T[];
  columns: FilterColumn<T>[];
}

export function useColumnFilters<T>(columns: FilterColumn<T>[]): ColumnFilterState<T> {
  const [values, setValues] = useState<Record<string, string>>({});

  const setValue = useCallback((key: string, value: string) => {
    setValues((vorher) => {
      const naechste = { ...vorher, [key]: value };
      if (value === '') delete naechste[key];
      return naechste;
    });
  }, []);

  const clear = useCallback(() => setValues({}), []);

  const active = Object.keys(values).length > 0;

  const apply = useCallback((rows: readonly T[]) => filterRows(rows, columns, values), [columns, values]);

  return useMemo(
    () => ({ values, setValue, active, clear, apply, columns }),
    [values, setValue, active, clear, apply, columns],
  );
}

const useStyles = makeStyles({
  // Gleiche Masse wie die bereits vorhandene Filterzeile der
  // Zuweisungsseite - zwei verschieden hohe Filterzeilen in einer Anwendung
  // sehen nach Versehen aus.
  cell: { paddingBlock: tokens.spacingVerticalXXS },
  input: { minWidth: '7rem', width: '100%' },
});

/**
 * Die Eingabezeile. Gehoert in den `TableHeader`, direkt hinter die Zeile mit
 * den Ueberschriften – damit sie beim Scrollen mit oben bleibt, wo der
 * Tabellenkopf das tut.
 */
export function ColumnFilterRow<T>({ state }: { state: ColumnFilterState<T> }) {
  const styles = useStyles();
  const { t } = useI18n();

  return (
    <TableRow>
      {state.columns.map((column) => (
        <TableHeaderCell key={column.key} className={styles.cell}>
          {column.disabled ? (
            // Sichtbar leer, aber vorhanden: Faellt die Zelle weg, verrutschen
            // alle folgenden Spalten gegenueber ihren Ueberschriften.
            state.active ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                onClick={state.clear}
                title={t('filter.clearAll')}
                aria-label={t('filter.clearAll')}
              />
            ) : null
          ) : (
            <Input
              size="small"
              className={styles.input}
              appearance="underline"
              contentBefore={<FilterRegular />}
              placeholder={t('common.filter')}
              aria-label={t('filter.forColumn', { column: column.label ?? column.key })}
              value={state.values[column.key] ?? ''}
              onChange={(_, d) => state.setValue(column.key, d.value)}
            />
          )}
        </TableHeaderCell>
      ))}
    </TableRow>
  );
}
